/**
=========================================================
* Material Dashboard 2 PRO React - v2.2.1
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// react-router-dom components
import { Link } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

// @mui material components
import Card from "@mui/material/Card";
import Checkbox from "@mui/material/Checkbox";
import Radio from "@mui/material/Radio";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import linearGradient from "assets/theme/functions/linearGradient";
import { CssBaseline, ThemeProvider } from "@mui/material";

// Internationalization
import { SUPPORTED_LANGUAGES, getLanguageConfig } from "i18n/languages";

// Material Dashboard 2 PRO React themes
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";

// Material Dashboard 2 PRO React Dark Mode themes
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";
import CoverLayout from "./login";
// Authentication layout components
// import CoverLayout from "layouts/authentication/components/CoverLayout";

// Images
import bgImage from "assets/images/lh/site-header-bg-2.jpg";
import PropTypes from "prop-types";
import { GoogleLogin } from "@react-oauth/google";

function Login({ onLogin }) {
  const { t, i18n } = useTranslation();
  const [darkMode, setDarkMode] = useState(true);
  const [tc, setTCStatus] = useState(false);

  const currentLangConfig = getLanguageConfig(i18n.language);
  const currentLang = currentLangConfig.code;

  const handleLanguageChange = (code) => {
    localStorage.setItem("language", code);
    i18n.changeLanguage(code);
  };

  const setTC = () => {
    setTCStatus(!tc);
  };

  return (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />

      <CoverLayout image={bgImage}>
        <Card>
          <MDBox
            variant="gradient"
            bgColor="dark"
            borderRadius="lg"
            coloredShadow="secondary"
            mx={2}
            mt={-3}
            p={3}
            mb={1}
            textAlign="center"
          >
            <MDTypography variant="h2" fontWeight="medium" color="white" mt={1}>
              ලක් හඳහන
            </MDTypography>
            {currentLangConfig.brandTransliteration && (
              <MDTypography
                variant="button"
                fontWeight="medium"
                color="white"
                sx={{
                  fontSize: "0.95rem",
                  opacity: 0.9,
                  display: "block",
                  mt: 0.25,
                  letterSpacing: "0.3px",
                }}
              >
                {currentLangConfig.brandTransliteration}
              </MDTypography>
            )}
            <MDTypography variant="h6" fontWeight="regular" color="white" mt={1} opacity={0.85}>
              {t("auth.appSubtitle", { defaultValue: "ජ්‍යෝතිෂ මෘදුකාංගය" })}
            </MDTypography>
          </MDBox>

          {/* Language Selector as Radio Options */}
          <MDBox px={3} pt={2} pb={1}>
            <MDBox display="flex" alignItems="center" mb={1.5} gap={0.75}>
              <Icon fontSize="small" color="primary">language</Icon>
              <MDTypography variant="caption" fontWeight="bold" color="text" textTransform="uppercase">
                {t("auth.selectLanguage", { defaultValue: "භාෂාව තෝරන්න / Select Language" })}
              </MDTypography>
            </MDBox>

            <Grid container spacing={1.25}>
              {SUPPORTED_LANGUAGES.map((lang) => {
                const isSelected = currentLang === lang.code;
                return (
                  <Grid item xs={6} key={lang.code}>
                    <MDBox
                      component="label"
                      onClick={() => handleLanguageChange(lang.code)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        p: 1,
                        borderRadius: "8px",
                        cursor: "pointer",
                        border: isSelected
                          ? "2px solid #2196f3"
                          : darkMode
                          ? "1px solid rgba(255, 255, 255, 0.18)"
                          : "1px solid rgba(0, 0, 0, 0.15)",
                        backgroundColor: isSelected
                          ? darkMode
                            ? "rgba(33, 150, 243, 0.18)"
                            : "rgba(33, 150, 243, 0.08)"
                          : darkMode
                          ? "rgba(255, 255, 255, 0.04)"
                          : "rgba(0, 0, 0, 0.02)",
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          borderColor: "#2196f3",
                          backgroundColor: darkMode
                            ? "rgba(33, 150, 243, 0.12)"
                            : "rgba(33, 150, 243, 0.05)",
                        },
                      }}
                    >
                      <Radio
                        checked={isSelected}
                        onChange={() => handleLanguageChange(lang.code)}
                        value={lang.code}
                        name="auth-language-radio"
                        size="small"
                        sx={{
                          p: 0.25,
                          color: isSelected ? "primary.main" : "text.secondary",
                          "&.Mui-checked": {
                            color: "primary.main",
                          },
                        }}
                      />
                      <MDBox ml={0.75} sx={{ overflow: "hidden" }}>
                        <MDTypography
                          variant="button"
                          fontWeight={isSelected ? "bold" : "medium"}
                          color={isSelected ? "primary" : darkMode ? "white" : "dark"}
                          sx={{ fontSize: "0.85rem", lineHeight: 1.2, display: "block" }}
                        >
                          {lang.label}
                        </MDTypography>
                        {lang.englishLabel && lang.label !== lang.englishLabel && (
                          <MDTypography
                            variant="caption"
                            color="text"
                            sx={{ fontSize: "0.7rem", opacity: 0.7, display: "block", lineHeight: 1 }}
                          >
                            {lang.englishLabel}
                          </MDTypography>
                        )}
                      </MDBox>
                    </MDBox>
                  </Grid>
                );
              })}
            </Grid>
          </MDBox>

          <Divider sx={{ my: 1.5, mx: 3 }} />

          <MDBox pt={1} pb={3} px={3}>
            <MDBox component="form" role="form">
              <MDBox display="flex" alignItems="flex-start" ml={-0.5} mb={2}>
                <Checkbox
                  checked={tc}
                  onChange={setTC}
                  sx={{ p: 0.5, mr: 1, mt: -0.2 }}
                />
                <MDTypography
                  variant="button"
                  fontWeight="regular"
                  color="text"
                  sx={{ cursor: "pointer", userSelect: "none", fontSize: "0.85rem", lineHeight: 1.4 }}
                >
                  {t("auth.agreePrefix")}
                  <MDTypography
                    component="a"
                    href="#"
                    variant="button"
                    fontWeight="bold"
                    color="info"
                    textGradient
                    onClick={(e) => {
                      e.preventDefault();
                      openTC();
                    }}
                    sx={{ fontSize: "0.85rem" }}
                  >
                    {t("auth.termsAndConditions")}
                  </MDTypography>
                  {t("auth.agreeSuffix") ? ` ${t("auth.agreeSuffix")}` : ""}
                </MDTypography>
              </MDBox>

              <MDBox mt={3} mb={1}>
                {!tc && (
                  <MDButton
                    onClick={() => setTCStatus(true)}
                    variant="gradient"
                    color="primary"
                    fullWidth
                  >
                    {t("auth.agreeButton", { defaultValue: "එකඟ වෙමි" })}
                  </MDButton>
                )}
                {tc && (
                  <>
                    <MDBox
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        textAlign: "center",
                        mb: 1.5,
                      }}
                    >
                      <MDTypography display="block" variant="h6" color={darkMode ? "white" : "dark"}>
                        {t("auth.googleLoginPrompt", { defaultValue: "ඔබගේ Google ගිණුම සමඟ ඇතුල් වන්න" })}
                      </MDTypography>
                    </MDBox>
                    <MDBox
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <GoogleLogin
                        disabled={!tc}
                        onSuccess={(credentialResponse) => {
                          onLogin(credentialResponse);
                        }}
                        onError={() => {
                          console.log("Login Failed");
                        }}
                        theme="outline"
                        logo_alignment="left"
                        accessType="offline"
                      />
                    </MDBox>
                  </>
                )}
              </MDBox>
            </MDBox>
          </MDBox>
        </Card>
      </CoverLayout>
    </ThemeProvider>
  );
}

export default Login;
function openTC() {
  return window.open(`${process.env.PUBLIC_URL}/tandc.html`, '_blank');
}

Login.propTypes = {
  onLogin: PropTypes.func.isRequired,
};

